/**
 * File Upload Area Component
 */
import React, { useEffect, useId, useRef } from "react";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import { deleteAdminUpload } from "../../actions/file.actions";

/**
 * Styles as objects for reuse - prevents style recreation on each render
 * while keeping the code cleaner than using styled-components
 */
const styles = {
  container: {
    mb: 2,
  },
  title: {
    mt: 0,
    mb: 1,
    fontWeight: 500,
  },
  dropZone: (theme, hasError) => ({
    maxWidth: "300px",
    position: "relative",
    cursor: "pointer",
    border: "2px dashed",
    borderColor: hasError
      ? theme.palette.error.main
      : theme.palette.primary.main,
    borderRadius: 1,
    p: 3,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: theme.transitions.create("background-color"),
    "&:hover": {
      bgcolor: "rgba(0, 0, 0, 0.04)",
    },
  }),
  filePreview: (theme, hasError) => ({
    maxWidth: "300px",
    display: "flex",
    alignItems: "center",
    mt: 2,
    p: 1,
    border: "1px solid",
    borderColor: hasError
      ? theme.palette.error.main
      : theme.palette.primary.main,
    borderRadius: 4,
    padding: "11px 10px",
    boxShadow: "0 0 9px 3px rgb(0 0 0 / 4%)",
    bgcolor: "action.hover",
    width: "100%",
  }),
  previewThumbnail: {
    width: 50,
    height: 50,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mr: 1.5,
    borderRadius: 1,
    bgcolor: "background.paper",
  },
  previewContent: {
    flex: 1,
    overflow: "hidden", // Keep to prevent expanding beyond the container
    wordBreak: "break-word", // Enable word breaking for very long filenames
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
};

/**
 * Reusable file upload area component
 * @param {Object} props
 * @param {string} props.title - Title for the upload section
 * @param {string|React.ReactNode} props.description - Optional description/notes for upload
 * @param {string} props.previewUrl - URL of file to preview (for images)
 * @param {string} props.fileName - Name of the file that was uploaded
 * @param {Function} props.onChange - Function to call when file is selected
 * @param {string} props.accept - File types to accept (.jpg, .pdf, etc.)
 * @param {boolean} props.error - Whether there's an error with the upload
 * @param {string} props.errorText - Error message to display
 * @param {boolean} props.showPreview - Whether to show preview (default true for images)
 * @component
 */
const FileUploadArea = ({
  title,
  description,
  previewUrl = "",
  fileName = "",
  onChange,
  accept = "*/*",
  error = false,
  errorText = "Please upload a file",
  showPreview = true,
  onRemove,
  showRemove = false,
  fileInputRef,
  deleteRequest,
  onDeleteSuccess,
}) => {
  const hiddenFileInput = useRef(null);
  const inputRef = fileInputRef || hiddenFileInput;
  const deleteDialogTitleId = useId();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");
  const isMountedRef = useRef(false);
  const deleteAbortRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      deleteAbortRef.current?.abort();
      deleteAbortRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof previewUrl !== "string" || !previewUrl.startsWith("blob:")) {
      return undefined;
    }

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleClickUpload = (e) => {
    e.stopPropagation();
    if (inputRef.current) {
      // Selecting the same file again must still produce a change event.
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files?.length || !onChange) {
      return;
    }

    // Keep dropped files on the same change-event path as Browse Files so
    // every consumer receives the event shape it already handles.
    onChange({ target: { files } });
  };

  // Determine if preview should be shown (only for images by default)
  const shouldShowPreview =
    showPreview &&
    typeof previewUrl === "string" &&
    (previewUrl.match(/\.(jpeg|jpg|gif|png)$/i) ||
      previewUrl.includes("data:image") ||
      previewUrl.startsWith("blob:"));

  const canDeletePersistedFile = Boolean(
    fileName &&
      (typeof previewUrl !== "string" || !previewUrl.startsWith("blob:")) &&
      deleteRequest?.resource &&
      Number(deleteRequest?.id) > 0 &&
      deleteRequest?.field,
  );

  const canRemoveLocalFile =
    typeof previewUrl === "string" && previewUrl.startsWith("blob:");

  const previewLink =
    typeof previewUrl === "string" &&
    (previewUrl.startsWith("http") || previewUrl.startsWith("blob:"));

  const handleDelete = async () => {
    if (deleteLoading || !canDeletePersistedFile) {
      return;
    }

    if (isMountedRef.current) {
      setDeleteLoading(true);
      setDeleteError("");
    }

    const abortController =
      typeof AbortController === "function" ? new AbortController() : null;
    deleteAbortRef.current = abortController;

    try {
      const response = await deleteAdminUpload(deleteRequest, {
        ...(abortController ? { signal: abortController.signal } : {}),
      });
      if (!isMountedRef.current) {
        return;
      }
      if (!response?.data?.status) {
        throw new Error(response?.data?.message || "Unable to delete upload.");
      }

      onRemove?.();
      onDeleteSuccess?.();
      if (isMountedRef.current) {
        setDeleteDialogOpen(false);
      }
    } catch (error) {
      if (isMountedRef.current && !abortController?.signal.aborted) {
        setDeleteError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to delete upload.",
        );
      }
    } finally {
      if (isMountedRef.current) {
        setDeleteLoading(false);
      }
      if (deleteAbortRef.current === abortController) {
        deleteAbortRef.current = null;
      }
    }
  };

  return (
    <Box sx={styles.container}>
      {title && (
        <Typography variant="h6" component="h5" sx={styles.title}>
          {title}
        </Typography>
      )}

      <Box
        data-testid="file-upload-dropzone"
        onClick={handleClickUpload}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onDrop={handleDrop}
        className={error ? "error-border" : ""}
        sx={(theme) => styles.dropZone(theme, error)}
      >
        <CloudUploadIcon fontSize="large" color={error ? "error" : "primary"} />

        <input
          type="file"
          ref={inputRef}
          accept={accept}
          onChange={onChange}
          onClick={(event) => event.stopPropagation()}
          style={{ display: "none" }}
        />

        <Typography
          variant="body1"
          sx={{
            my: 1,
            fontWeight: "medium",
            textAlign: "center",
            wordBreak: "break-all",
            border: "1px dotted #BBB",
          }}
        >
          {fileName || "Drag & Drop files or Click to browse"}
        </Typography>

        {description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", display: "block", mb: 1 }}
          >
            {description}
          </Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleClickUpload}
          sx={{ mt: 1 }}
        >
          Browse Files
        </Button>
      </Box>

      {error && (
        <Typography
          color="error"
          variant="caption"
          sx={{ mt: 1, display: "block" }}
        >
          {errorText}
        </Typography>
      )}

      {/* Universal file preview  */}
      {fileName && (
        <Box sx={(theme) => styles.filePreview(theme, error)}>
          <Box sx={styles.previewThumbnail}>
            {shouldShowPreview ? (
              <Tooltip title="Open in a new tab" arrow>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={`Preview of ${fileName}`}
                    sx={styles.previewImage}
                  />
                </a>
              </Tooltip>
            ) : (
              <InsertDriveFile color="action" />
            )}
          </Box>
          <Box sx={styles.previewContent}>
            <Typography variant="body2" sx={{ pr: 1 }}>
              {previewLink ? (
                <Tooltip title="Open in a new tab" arrow>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {fileName}
                  </a>
                </Tooltip>
              ) : (
                fileName
              )}
            </Typography>
          </Box>
          {onRemove && (showRemove || canRemoveLocalFile) && (
            <Button
              type="button"
              color="error"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                if (inputRef.current) {
                  inputRef.current.value = "";
                }
                onRemove();
              }}
            >
              Remove
            </Button>
          )}
          {canDeletePersistedFile && (
            <Button
              type="button"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={(event) => {
                event.stopPropagation();
                setDeleteError("");
                setDeleteDialogOpen(true);
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteDialogOpen(false);
          }
        }}
        aria-labelledby={deleteDialogTitleId}
      >
        <DialogTitle id={deleteDialogTitleId}>Delete upload?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the file and clears its saved reference. This action
            cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Typography
              role="alert"
              color="error"
              variant="body2"
              sx={{ mt: 2 }}
            >
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            autoFocus
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileUploadArea;
